var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { FontStyle } from '@inter-ikea-kompis/themes';
import { DexfSettingsUtility, PriceFormatterUtility } from '@inter-ikea-kompis/utilities';
import { Hyperlink } from '@inter-ikea-kompis/component-hyperlink';
import { KeyboardInjectorFinder } from '@inter-ikea-kompis/component-keyboard';
import { Text } from '@inter-ikea-kompis/component-text';
import * as SkapaStyle from '@ingka/common-styles-webc';
import { ModalFooter as SkapaModalFooter, ModalHeader as SkapaModalHeader, Prompt as SkapaPrompt, Sheet as SkapaSheet } from '@ingka/modal-webc/passive.js';
import EcoFeeModalEnum from '../../enums/EcoFeeModalEnum.js';
import EcoFeeCard from '../eco-fee-card/EcoFeeCard.js';
import EcoFeeStyle from './EcoFeeStyle.js';
/**
 * Eco Fee is a legal requirement in some markets. This eco fee is applicable for Electronics and Furniture.
 *
 * The Eco Fee component consists of a disclaimer link which is used in the related Kompis price display components. The link opens a separate dialogue with the legal information text and the calculated eco fee.
 *
 * When and where the Eco Fee should be displayed can be found in the {@link https://i-p-e-x.atlassian.net/wiki/spaces/BOK/pages/2790850646/Eco-fee Range Experience BOK requirements}.
 *
 * @event modalOpen Triggered when the user clicks on the eco fee link
 * @event modalClose Triggered when the user clicks on the sheet close button.
 */
export default class EcoFee extends KompisElement {
    constructor() {
        super(...arguments);
        // Accepts a object format corresponding to { ShoppingProduct } from '@inter-ikea-kompis/types';
        this.totalPrice = null;
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // Visible modal
        this.visibleModal = null;
        // Disables links. This attribute should be set to "true" when in a Kiosk.
        this.disableLinks = false;
        this.sheetElement = null;
        this.triggerElement = null;
        this.dexfSettings = null;
    }
    render() {
        // We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslationsAndSettings()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        if (!this.totalPrice || !this.getSettings().kompis.localization.hasEcoPart) {
            return null;
        }
        return html `
			<style>
				${EcoFeeStyle.getStyle({ theme: this.theme })}
			</style>

			${this.getFurnitureDisclaimer()} ${this.getElectronicsDisclaimer()}
		`;
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.closeSheet();
    }
    async connectedCallback() {
        this.customElements.define('skapa-sheet', SkapaSheet);
        this.customElements.define('skapa-modal-footer', SkapaModalFooter);
        this.customElements.define('skapa-modal-header', SkapaModalHeader);
        this.customElements.define('skapa-prompt', SkapaPrompt);
        this.customElements.define('kompis-hyperlink', Hyperlink);
        this.customElements.define('kompis-text', Text);
        this.customElements.define('kompis-eco-fee-card', EcoFeeCard);
        super.connectedCallback();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        if (this.dexfSettings &&
            this.dexfSettings.kompis.localization.hasEcoPart &&
            changedProperties.has('visibleModal')) {
            if (this.visibleModal === EcoFeeModalEnum.ecoFeeCard) {
                this.openSheet();
            }
            else {
                this.closeSheet();
            }
        }
        return true;
    }
    hasTranslationsAndSettings() {
        return (!!this.translations && !!this.settings) || !!this.localizedInformation;
    }
    getSettings() {
        return this.dexfSettings;
    }
    getTranslations() {
        const translations = this.translations
            ? this.translations
            : this.localizedInformation?.translations;
        return translations;
    }
    getFurnitureDisclaimer() {
        if (!this.totalPrice?.ecoPrice) {
            return null;
        }
        const { ecoOfWhich, ecoArticleFurnitureShort } = this.getTranslations();
        const formattedPrice = PriceFormatterUtility.format(this.totalPrice.ecoPrice, this.getSettings())
            .map((item) => item.value)
            .join('');
        const disclaimer = [ecoOfWhich, ecoArticleFurnitureShort].join(' ');
        const linkText = `${disclaimer} ${formattedPrice}`;
        return this.getLink(linkText);
    }
    getElectronicsDisclaimer() {
        if (!this.totalPrice?.weeePrice) {
            return null;
        }
        const { ecoOfWhich, ecoArticleElectronicsShort } = this.getTranslations();
        const formattedPrice = PriceFormatterUtility.format(this.totalPrice.weeePrice, this.getSettings())
            .map((item) => item.value)
            .join('');
        const disclaimer = [ecoOfWhich, ecoArticleElectronicsShort].join(' ');
        const linkText = `${disclaimer} ${formattedPrice}`;
        return this.getLink(linkText);
    }
    getLink(text) {
        return html `
			<kompis-text .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.bodySmall}">
				<kompis-hyperlink
					.theme="${this.theme}"
					.text="${text}"
					.underline="${true}"
					@linkClick="${(event) => this.onLinkClick(event)}"
				>
				</kompis-hyperlink>
			</kompis-text>
		`;
    }
    onLinkClick(event) {
        this.triggerElement = event.target?.shadowRoot?.querySelector('a') ?? null;
        this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: {
                visibleModal: EcoFeeModalEnum.ecoFeeCard,
                triggerElement: this.triggerElement
            }
        }));
    }
    closeSheet() {
        if (this.sheetElement) {
            this.sheetElement.open = false;
            this.sheetElement = null;
        }
    }
    openSheet() {
        if (this.sheetElement) {
            return;
        }
        const sheetElement = this.createSheetElement();
        this.sheetElement = sheetElement;
        const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
        parent.appendChild(sheetElement);
        sheetElement.open = true;
    }
    createSheetElement() {
        const sheetElement = this.createSheet();
        const sheetHeaderElement = this.createSheetHeader();
        const ecoFeeCardElement = this.createCard();
        sheetElement.appendChild(sheetHeaderElement);
        sheetElement.appendChild(ecoFeeCardElement);
        return sheetElement;
    }
    createSheet() {
        const sheetElement = this.createInternalElement('skapa-sheet');
        const ecoHeadline = this.getTranslations().ecoHeadline;
        sheetElement.setAttribute('aria-label', ecoHeadline);
        sheetElement.size = 'small';
        sheetElement.fullHeight = true;
        sheetElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
        sheetElement.style.zIndex = '500';
        sheetElement.addEventListener('closerequest', () => {
            if (this.sheetElement) {
                this.dispatchEvent(new CustomEvent('modalClose', {
                    bubbles: true,
                    detail: {
                        visibleModal: null,
                        triggerElement: this.triggerElement
                    }
                }));
            }
        });
        sheetElement.addEventListener('closed', () => {
            sheetElement.parentElement?.removeChild(sheetElement);
        });
        return sheetElement;
    }
    createSheetHeader() {
        const sheetHeaderElement = this.createInternalElement('skapa-modal-header');
        sheetHeaderElement.slot = 'header';
        const { ecoHeadline, modalAriaLabelButtonClose } = this.getTranslations();
        const heading = document.createElement('h2');
        heading.innerText = ecoHeadline;
        heading.className = SkapaStyle.Text.classnames({ heading: { s: true } });
        heading.style.margin = '0';
        const style = document.createElement('style');
        style.textContent = SkapaStyle.Text.CSS;
        heading.appendChild(style);
        const headerSpan = document.createElement('span');
        headerSpan.slot = 'closebutton-label';
        headerSpan.innerText = modalAriaLabelButtonClose;
        sheetHeaderElement.appendChild(heading);
        sheetHeaderElement.appendChild(headerSpan);
        return sheetHeaderElement;
    }
    createCard() {
        const cardElement = this.createInternalElement('kompis-eco-fee-card');
        cardElement.theme = this.theme;
        cardElement.settings = this.settings;
        cardElement.translations = this.translations;
        cardElement.localizedInformation = this.localizedInformation;
        cardElement.totalPrice = this.totalPrice;
        if (this.disableLinks) {
            cardElement.disableLinks = true;
        }
        return cardElement;
    }
}
__decorate([
    property({ type: Object })
], EcoFee.prototype, "totalPrice", void 0);
__decorate([
    property({ type: Object })
], EcoFee.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], EcoFee.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], EcoFee.prototype, "settings", void 0);
__decorate([
    property({ type: Object, required: true })
], EcoFee.prototype, "theme", void 0);
__decorate([
    property({ type: String })
], EcoFee.prototype, "visibleModal", void 0);
__decorate([
    property({ type: Boolean })
], EcoFee.prototype, "disableLinks", void 0);
//# sourceMappingURL=EcoFee.js.map